// src/components/admin/diagnostics/DiagnosticReports.js
//
// Unified Diagnostic Reports page, redesigned as a log/history view modeled on
// the Session Log page (SessionLogWorkspace). Instead of an empty page that only
// shows reports once a student is picked, every SAT / PSAT / ACT diagnostic
// report is listed in a table by default. A filter bar on top (improved student
// dropdown with test-type pills, test-type filter, date range) lets admins
// browse, search, filter, and open reports — the same way Session Log works.
//
// Clicking a row still opens the existing full report page in a new tab via the
// `getReportUrl` prop; the report pages themselves are unchanged. The prop lets
// each portal supply its own URL scheme (admin uses real routes; the tutor portal
// uses standalone /tutor routes).

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import SearchableStudentDropdown from "../dsat/reports/SearchableStudentDropdown";
import { fetchAllDiagnosticData, deleteDiagnosticAttempt } from "./diagnosticReportsData";
import { Pagination } from "../../session_logs/ProgressReportWorkspace";

import "../../session_logs/ProgressReportWorkspace.css"; // shared design tokens
import "./diagnosticReportsLog.css"; // small filter-grid override

// ── Test-type pills ───────────────────────────────────────────────────────────
const TYPE_PILL = {
  SAT: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  PSAT: { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
  ACT: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
};

function TestTypePill({ type }) {
  const c = TYPE_PILL[type] || { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3px",
        background: c.bg,
        color: c.color,
        border: `1px solid ${c.border}`,
        lineHeight: 1.4,
        whiteSpace: "nowrap",
      }}
    >
      {type}
    </span>
  );
}

function TestTypePills({ types }) {
  if (!types || types.length === 0) return null;
  return (
    <span style={{ display: "inline-flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
      {types.map((t) => (
        <TestTypePill key={t} type={t} />
      ))}
    </span>
  );
}

// Status badge reusing the shared prw-badge palette.
function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "prw-badge--completed";
  if (s.includes("partial")) return "prw-badge--partial";
  return "prw-badge--neutral";
}

// ── Report row ──────────────────────────────────────────────────────────────────
function ReportRow({ attempt, onOpen, onContextMenu }) {
  return (
    <tr
      className="prw-clickableRow"
      onClick={() => onOpen(attempt)}
      onContextMenu={(e) => onContextMenu(e, attempt)}
    >
      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{attempt.completedLabel}</td>
      <td>{attempt.studentName || "—"}</td>
      <td>
        <TestTypePill type={attempt.type} />
      </td>
      <td>
        {attempt.title}
        {attempt.isArchived && (
          <span
            style={{
              marginLeft: 8,
              padding: "1px 7px",
              borderRadius: 6,
              fontSize: 10,
              fontWeight: 700,
              color: "#94a3b8",
              border: "1px solid #e2e8f0",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
            }}
          >
            Archived
          </span>
        )}
      </td>
      <td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>{attempt.score}</td>
      <td>
        <span className={`prw-badge ${statusBadgeClass(attempt.status)}`}>
          {String(attempt.status).toLowerCase()}
        </span>
      </td>
      <td style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>Open ↗</td>
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
const REPORTS_PER_PAGE = 20;

export default function DiagnosticReports({
  getReportUrl,
  mode = "active", // "active" → non-archived only; "archived" → archived only
  title,
  subtitle,
  canDelete = false, // only admins may permanently delete diagnostic data
}) {
  const isArchivedMode = mode === "archived";
  const heading = title || (isArchivedMode ? "Archived Diagnostic Reports" : "Diagnostic Reports");
  const blurb =
    subtitle ||
    (isArchivedMode
      ? "Browse, search, and filter archived SAT, PSAT, and ACT diagnostic reports. Click any row to open the full report in a new tab."
      : "Browse, search, and filter all SAT, PSAT, and ACT diagnostic reports. Click any row to open the full report in a new tab.");

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlStudentId = String(searchParams.get("studentId") || "").trim();

  // Right-click context menu state ({ x, y, attempt })
  const [rowMenu, setRowMenu] = useState(null);
  const [deletingId, setDeletingId] = useState("");

  const [students, setStudents] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [selectedStudentId, setSelectedStudentId] = useState(urlStudentId);
  const [testTypeFilter, setTestTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);

  // Load all data for the current mode (active vs archived)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { students: s, attempts: a } = await fetchAllDiagnosticData({ archived: isArchivedMode });
        if (!cancelled) {
          setStudents(s);
          setAttempts(a);
        }
      } catch (e) {
        if (!cancelled) setError(`Failed to load diagnostic reports: ${e?.message || e}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isArchivedMode]);

  // Reset to first page when any filter changes
  useEffect(() => {
    setPage(1);
  }, [selectedStudentId, testTypeFilter, startDate, endDate]);

  // Test-type options derived from the data actually present
  const availableTypes = useMemo(() => {
    const set = new Set(attempts.map((a) => a.type));
    return ["SAT", "PSAT", "ACT"].filter((t) => set.has(t));
  }, [attempts]);

  const filtered = useMemo(() => {
    const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const endMs = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;
    return attempts.filter((a) => {
      if (selectedStudentId && a.studentId !== selectedStudentId) return false;
      if (testTypeFilter !== "all" && a.type !== testTypeFilter) return false;
      const ms = a.completedMs || 0;
      if (startMs !== null && ms < startMs) return false;
      if (endMs !== null && ms > endMs) return false;
      return true;
    });
  }, [attempts, selectedStudentId, testTypeFilter, startDate, endDate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / REPORTS_PER_PAGE));
  const pagedReports = filtered.slice((page - 1) * REPORTS_PER_PAGE, page * REPORTS_PER_PAGE);

  const selectedStudent = useMemo(
    () => students.find((s) => s.uid === selectedStudentId) || null,
    [students, selectedStudentId]
  );

  const openReport = (attempt) => {
    const url = typeof getReportUrl === "function" ? getReportUrl(attempt) : null;
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openReportSameTab = (attempt) => {
    const url = typeof getReportUrl === "function" ? getReportUrl(attempt) : null;
    if (url) navigate(url);
  };

  const openRowMenu = (e, attempt) => {
    e.preventDefault();
    setRowMenu({ x: e.clientX, y: e.clientY, attempt });
  };

  // Close the context menu on any outside click / scroll.
  useEffect(() => {
    if (!rowMenu) return undefined;
    const close = () => setRowMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [rowMenu]);

  const handleDeleteAttempt = async (attempt) => {
    const who = attempt.studentName || "this student";
    const ok = window.confirm(
      `Permanently delete this ${attempt.type} diagnostic for ${who}?\n\n` +
        "This removes the report, all saved responses/progress, and the test-access record, " +
        "so the diagnostic can be assigned or taken again cleanly.\n\n" +
        "This action CANNOT be undone."
    );
    if (!ok) return;
    try {
      setDeletingId(attempt.attemptId);
      await deleteDiagnosticAttempt(attempt);
      setAttempts((prev) => prev.filter((a) => a.attemptId !== attempt.attemptId));
    } catch (e) {
      alert(`Failed to delete diagnostic: ${e?.message || e}`);
    } finally {
      setDeletingId("");
    }
  };

  const metaText = loading
    ? "Loading…"
    : error
    ? "Error loading reports"
    : `${filtered.length} report${filtered.length !== 1 ? "s" : ""}${
        selectedStudent ? ` for ${selectedStudent.name}` : ""
      } — click a row to open in a new tab`;

  return (
    <div className="prw-shell">
      <div className="prw-pageHeader">
        <h1>{heading}</h1>
        <p>{blurb}</p>
      </div>

      {/* ── Filter Card ── */}
      <div className="prw-card">
        <div className="prw-cardTitle">Filter Reports</div>
        <div className="prw-filterRow diaglog-filterRow">
          {/* Student dropdown with test-type pills + clear */}
          <div className="prw-fieldGroup">
            <div className="prw-label">Student</div>
            <div className="prw-dropdownWrap">
              <SearchableStudentDropdown
                students={students}
                selectedId={selectedStudentId}
                onSelect={(uid) => setSelectedStudentId(uid)}
                placeholder={loading ? "Loading students…" : "All students"}
                disabled={loading}
                getOptionSecondaryText={(s) =>
                  s.testTypes && s.testTypes.length ? <TestTypePills types={s.testTypes} /> : "No reports"
                }
                getTriggerSecondaryText={(s) =>
                  s.testTypes && s.testTypes.length ? <TestTypePills types={s.testTypes} /> : "No reports"
                }
                getOptionRightText={(s) => (
                  <span
                    style={{
                      display: "inline-flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 2,
                      lineHeight: 1.2,
                    }}
                  >
                    {s.lastDateLabel && s.lastDateLabel !== "N/A" && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>
                        {s.lastDateLabel}
                      </span>
                    )}
                    {s.reportCount > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8" }}>
                        {s.reportCount} report{s.reportCount !== 1 ? "s" : ""}
                      </span>
                    )}
                  </span>
                )}
              />
              {selectedStudentId && (
                <button
                  type="button"
                  className="prw-clearBtn"
                  title="Clear student filter"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStudentId("");
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Test type */}
          <div className="prw-fieldGroup">
            <div className="prw-label">Test Type</div>
            <select
              className="prw-input"
              value={testTypeFilter}
              onChange={(e) => setTestTypeFilter(e.target.value)}
              disabled={loading}
            >
              <option value="all">All types</option>
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div className="prw-fieldGroup">
            <div className="prw-label">Start Date</div>
            <input
              type="date"
              className="prw-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">End Date</div>
            <input
              type="date"
              className="prw-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* ── Diagnostic Reports Table ── */}
      <div className="prw-card">
        <div className="prw-sectionHeader">
          <div className="prw-sectionTitle">Diagnostic Reports</div>
          <div className="prw-sectionMeta">{metaText}</div>
        </div>

        {error && <div className="prw-errorBox" style={{ marginBottom: 12 }}>{error}</div>}

        <div className="prw-tableWrap">
          <table className="prw-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Student</th>
                <th>Type</th>
                <th>Test / Attempt</th>
                <th>Score</th>
                <th>Status</th>
                <th>Report</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className="prw-emptyRow">
                  <td colSpan={7}>Loading diagnostic reports…</td>
                </tr>
              )}
              {!loading && pagedReports.length === 0 && (
                <tr className="prw-emptyRow">
                  <td colSpan={7}>No diagnostic reports found for the selected filters.</td>
                </tr>
              )}
              {!loading &&
                pagedReports.map((a) => (
                  <ReportRow
                    key={a.attemptId}
                    attempt={a}
                    onOpen={openReport}
                    onContextMenu={openRowMenu}
                  />
                ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} onPage={setPage} />

        <div className="prw-sectionMeta" style={{ marginTop: 8 }}>
          Tip: right-click a report for more actions (open, open in new tab{canDelete ? ", delete" : ""}).
          {deletingId && " · Deleting…"}
        </div>
      </div>

      {/* Right-click context menu */}
      {rowMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed",
            top: rowMenu.y,
            left: rowMenu.x,
            zIndex: 9999,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 12px 30px rgba(15,23,42,0.18)",
            padding: 5,
            minWidth: 210,
          }}
        >
          {[
            { label: "Open", onClick: () => openReportSameTab(rowMenu.attempt) },
            { label: "Open in new tab", onClick: () => openReport(rowMenu.attempt) },
            ...(canDelete
              ? [
                  {
                    label: "Delete diagnostic report",
                    danger: true,
                    onClick: () => handleDeleteAttempt(rowMenu.attempt),
                  },
                ]
              : []),
          ].map((it, i) => (
            <button
              key={i}
              onClick={() => {
                const fn = it.onClick;
                setRowMenu(null);
                fn();
              }}
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                border: "none",
                background: "none",
                textAlign: "left",
                fontSize: 13,
                fontWeight: it.danger ? 600 : 500,
                color: it.danger ? "#dc2626" : "#0f172a",
                padding: "8px 9px",
                borderRadius: 7,
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = it.danger ? "#fef2f2" : "#f1f5f9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "none";
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
